import React, { useState, useRef, useEffect } from "react";
import { Modal, Input, Select, Button, message } from "antd";
import { BsTrash } from "react-icons/bs";
import { FiImage } from "react-icons/fi";
import { updateCategory } from "../../../api/AdminRequest";
import IconPicker from "../../common/IconPicker";
import DynamicIcon from "../../common/DynamicIcon";

function EditCategoryModal({ visible, onClose, category, onSuccess }) {
    const [name, setName] = useState("");
    const [status, setStatus] = useState("active");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const fileInputRef = useRef(null);

    // Icon picker state
    const [selectedIcon, setSelectedIcon] = useState(null);
    const [iconPickerVisible, setIconPickerVisible] = useState(false);

    // Initialize form with category data when modal opens
    useEffect(() => {
        if (category && visible) {
            setName(category.name || "");
            setStatus(category.status || "active");
            setImagePreview(category.imageUrl || null);
            setSelectedIcon(category.icon || null);
            setImageFile(null); // Reset file, we'll only send if changed
        }
    }, [category, visible]);

    // Image handlers
    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                message.error("Please upload an image file");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                message.error("Image size should be less than 5MB");
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            message.error("Please enter category name");
            return;
        }
        if (!selectedIcon) {
            message.error("Please select a category icon");
            return;
        }
        if (!imagePreview) {
            message.error("Please upload a category image");
            return;
        }

        try {
            setSubmitLoading(true);

            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("status", status);
            formData.append("icon", JSON.stringify(selectedIcon));

            // Only append image if a new one was selected
            if (imageFile) {
                formData.append("image", imageFile);
            }

            const { data } = await updateCategory(category._id, formData);
            if (data?.success) {
                message.success("Category updated successfully!");
                onSuccess?.();
                onClose();
            }
        } catch (error) {
            console.log(error);
            message.error(error?.response?.data?.message || "Failed to update category");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleClose = () => {
        setName("");
        setStatus("active");
        setImageFile(null);
        setImagePreview(null);
        setSelectedIcon(null);
        onClose();
    };

    return (
        <Modal
            title="Edit Category"
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={500}
            destroyOnClose
        >
            <div className="space-y-6 pt-4">
                {/* Image Upload */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Image <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                    />
                    {imagePreview ? (
                        <div className="relative inline-block">
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                            />
                            <button
                                onClick={removeImage}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                                <BsTrash className="text-sm" />
                            </button>
                        </div>
                    ) : (
                        <div
                            onClick={handleImageClick}
                            className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#1955EB] hover:bg-gray-50 transition-colors"
                        >
                            <FiImage className="text-3xl text-gray-400 mb-2" />
                            <span className="text-xs text-gray-500">Click to upload</span>
                        </div>
                    )}
                </div>

                {/* Icon Picker */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Icon <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setIconPickerVisible(true)}
                            className={`
                                w-16 h-16 rounded-lg border-2 flex items-center justify-center
                                transition-all duration-200 cursor-pointer
                                ${selectedIcon
                                    ? "border-[#1955EB] bg-blue-50"
                                    : "border-dashed border-gray-300 hover:border-[#1955EB] hover:bg-gray-50"
                                }
                            `}
                        >
                            {selectedIcon ? (
                                <DynamicIcon icon={selectedIcon} className="text-2xl text-[#1955EB]" />
                            ) : (
                                <span className="text-gray-400 text-xs text-center">Select<br />Icon</span>
                            )}
                        </button>
                        {selectedIcon && (
                            <div className="text-sm text-gray-500">
                                <span className="font-medium">{selectedIcon.name}</span>
                                <button
                                    type="button"
                                    onClick={() => setSelectedIcon(null)}
                                    className="ml-2 text-red-500 hover:text-red-700"
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Category Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                        placeholder="Enter category name (e.g., Vehicles)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="large"
                    />
                </div>

                {/* Status */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                    </label>
                    <Select
                        value={status}
                        onChange={(val) => setStatus(val)}
                        className="w-full"
                        size="large"
                        options={[
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ]}
                    />
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button onClick={handleClose} size="large">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={submitLoading}
                        size="large"
                        className="bg-[#1955EB] hover:bg-[#1945E0]"
                    >
                        Update Category
                    </Button>
                </div>
            </div>

            {/* Icon Picker Modal */}
            <IconPicker
                visible={iconPickerVisible}
                onClose={() => setIconPickerVisible(false)}
                onSelect={setSelectedIcon}
                selectedIcon={selectedIcon}
            />
        </Modal>
    );
}

export default EditCategoryModal;
